import { useState } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeSimple, WhatsappLogo, ArrowRight, Globe, ShieldCheck, Package } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEOHead from '../components/SEOHead';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { usePublicT } from '../i18n/usePublicT';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AboutPage() {
  const pt = usePublicT();
  const [contactData, setContactData] = useState({ name: '', email: '', company: '', message: '' });

  const handleContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/contact`, contactData);
      toast.success('Message sent!');
      setContactData({ name: '', email: '', company: '', message: '' });
    } catch { toast.error('Failed to send message'); }
  };

  const cards = [
    { icon: Package, titleKey: 'about.carriers', descKey: 'about.carriersDesc', color: '#FF9900' },
    { icon: Globe, titleKey: 'about.trilingualTitle', descKey: 'about.trilingualDesc', color: '#0080FF' },
    { icon: ShieldCheck, titleKey: 'about.secureTitle', descKey: 'about.secureDesc', color: '#10B981' },
  ];

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <SEOHead
        title="About & Contact — Schedule a Demo"
        description="Learn about Track My Package. Schedule a demo, contact our team."
        path="/about"
        keywords="courier software company, shipping management contact, logistics SaaS demo"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'Organization', name: 'Track My Package', url: 'https://trackmypackage.app', contactPoint: { '@type': 'ContactPoint', email: 'info@trackmypackage.app', contactType: 'customer service' } }}
      />

      <PublicNav activePage="/about" />

      {/* Hero */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">{pt('about.heroTitle')}</h1>
          <p className="text-gray-400 mt-4 text-base sm:text-lg">{pt('about.heroSubtitle')}</p>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((item, i) => (
            <motion.div key={i} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 text-center"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <item.icon size={32} style={{ color: item.color }} weight="duotone" className="mx-auto mb-3" />
              <h3 className="font-bold text-white text-base mb-1">{pt(item.titleKey)}</h3>
              <p className="text-sm text-gray-400">{pt(item.descKey)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-6 bg-[#060B15]" id="contact">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">{pt('about.getInTouch')}</h2>
            <p className="text-gray-400 text-sm mb-6">{pt('about.getInTouchDesc')}</p>
            <form onSubmit={handleContact} className="space-y-3">
              <Input value={contactData.name} onChange={e => setContactData({ ...contactData, name: e.target.value })} placeholder={pt('about.yourName')} className="rounded-xl h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="about-name" />
              <Input type="email" value={contactData.email} onChange={e => setContactData({ ...contactData, email: e.target.value })} placeholder={pt('signup.email')} className="rounded-xl h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" required data-testid="about-email" />
              <Input value={contactData.company} onChange={e => setContactData({ ...contactData, company: e.target.value })} placeholder={pt('about.companyName')} className="rounded-xl h-11 bg-white/5 border-white/10 text-white placeholder:text-gray-500" data-testid="about-company" />
              <textarea value={contactData.message} onChange={e => setContactData({ ...contactData, message: e.target.value })} placeholder={pt('about.tellUs')} className="w-full rounded-xl p-3 bg-white/5 border border-white/10 text-white placeholder:text-gray-500 text-sm min-h-[120px] focus:outline-none focus:ring-1 focus:ring-[#FF9900]" required data-testid="about-message" />
              <Button type="submit" className="w-full bg-[#003166] hover:bg-[#003166]/90 text-white rounded-xl py-5 text-base font-medium" data-testid="about-submit">
                {pt('about.sendMessage')} <ArrowRight size={16} className="ml-2" />
              </Button>
            </form>
          </div>
          <div className="flex flex-col gap-5">
            <motion.div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6"
              initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h3 className="font-bold text-white text-lg mb-3">{pt('about.scheduleDemo')}</h3>
              <p className="text-gray-400 text-sm mb-4">{pt('about.scheduleDemoDesc')}</p>
              <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')} className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white rounded-xl w-full py-5 text-base" data-testid="about-schedule-btn">
                {pt('nav.scheduleCall')} <ArrowRight size={16} className="ml-2" />
              </Button>
            </motion.div>
            <div className="space-y-3">
              <a href="mailto:info@trackmypackage.app" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors text-sm">
                <EnvelopeSimple size={18} /> info@trackmypackage.app
              </a>
              <a href="https://wa.me/+50688888888" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors text-sm">
                <WhatsappLogo size={18} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
